/**
 * The example gallery.
 *
 * Loading an example replaces the document, so it asks first whenever there is
 * unsaved work to lose. Destructive-by-surprise is the one thing a gallery must
 * not be.
 */
import { useMemo, useState } from 'react';
import { Button, Dialog, DialogFooter, EmptyState, Sparkles, Tabs } from '../ui';
import type { TabItem } from '../ui';
import { EXAMPLES } from '../../examples';
import type { ExampleCategory, ExampleDefinition } from '../../examples';
import type { Locale, Translator } from '../../i18n';
import type { MessageKey } from '../../i18n';

export interface ExamplesDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onLoad: (example: ExampleDefinition) => void;
  readonly needsConfirmation: boolean;
  readonly locale: Locale;
  readonly t: Translator['t'];
}

type Filter = ExampleCategory | 'all';

const CATEGORY_ORDER: readonly Filter[] = [
  'all',
  'basics',
  'controlFlow',
  'functions',
  'patterns',
  'algorithms',
];

export function ExamplesDialog({
  open,
  onClose,
  onLoad,
  needsConfirmation,
  locale,
  t,
}: ExamplesDialogProps) {
  const [filter, setFilter] = useState<Filter>('all');
  const [pending, setPending] = useState<ExampleDefinition | null>(null);

  const visible = useMemo(
    () => (filter === 'all' ? EXAMPLES : EXAMPLES.filter((e) => e.category === filter)),
    [filter],
  );

  const tabs: TabItem<Filter>[] = CATEGORY_ORDER.map((category) => ({
    id: category,
    label: t(`examples.category.${category}` as MessageKey),
  }));

  const choose = (example: ExampleDefinition): void => {
    if (needsConfirmation) setPending(example);
    else {
      onLoad(example);
      onClose();
    }
  };

  const title = (example: ExampleDefinition): string =>
    locale === 'pt' ? example.titlePt : example.titleEn;
  const description = (example: ExampleDefinition): string =>
    locale === 'pt' ? example.descriptionPt : example.descriptionEn;

  return (
    <>
      <Dialog
        open={open && pending === null}
        onClose={onClose}
        title={t('examples.title')}
        description={t('examples.subtitle')}
        size="lg"
      >
        <Tabs
          items={tabs}
          value={filter}
          onValueChange={setFilter}
          label={t('examples.title')}
          className="mb-4"
        />

        {visible.length === 0 ? (
          <EmptyState icon={<Sparkles />} title={t('examples.empty')} />
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {visible.map((example) => (
              <li key={example.id}>
                <button
                  type="button"
                  onClick={() => choose(example)}
                  className="flex h-full w-full flex-col gap-1 rounded-panel border border-border bg-surface p-3 text-left transition-colors hover:border-accent hover:bg-surface-sunken"
                >
                  <span className="text-sm font-semibold text-ink">{title(example)}</span>
                  <span className="text-xs text-ink-muted">{description(example)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={t('examples.loadConfirmTitle')}
        description={t('examples.loadConfirmBody')}
        size="sm"
        footer={
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)}>
              {t('examples.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (pending) onLoad(pending);
                setPending(null);
                onClose();
              }}
            >
              {t('examples.load')}
            </Button>
          </DialogFooter>
        }
      />
    </>
  );
}
